import { useEffect, useState } from "react";
import { api } from "./api";

interface Status {
  worker: {
    status: "ok" | "stale" | "none";
    lastSeenSecondsAgo: number | null;
    busy: boolean;
    currentStage: string | null;
  };
  queue: { runnable: number; waitingOnPeople: number; failed: number; oldestRunnableSeconds: number | null };
}

function ago(s: number | null): string {
  if (s === null) return "never";
  if (s < 90) return `${s} seconds ago`;
  if (s < 5400) return `${Math.round(s / 60)} minutes ago`;
  if (s < 172800) return `${Math.round(s / 3600)} hours ago`;
  return `${Math.round(s / 86400)} days ago`;
}

/**
 * Quiet when healthy or idle; loud only when the processor has stopped and
 * work is waiting on it -- the case that would otherwise surface days later,
 * from a client asking where their minutes are.
 */
export function ProcessorStatus() {
  const [s, setS] = useState<Status | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => api.get<Status>("/api/system/status")
      .then((r) => { if (live) setS(r); })
      .catch(() => {});
    void load();
    const t = setInterval(load, 30_000);
    return () => { live = false; clearInterval(t); };
  }, []);

  if (!s) return null;
  const { worker, queue } = s;
  const n = queue.runnable;
  const failed = queue.failed > 0
    ? ` · ${queue.failed} failed` : "";

  if (worker.status !== "ok" && n > 0) {
    return (
      <div className="alert" role="alert">
        <strong>Processing is paused.</strong>{" "}
        {worker.status === "none"
          ? "The processor has never checked in."
          : `The processor last checked in ${ago(worker.lastSeenSecondsAgo)}.`}{" "}
        {n} meeting{n === 1 ? " is" : "s are"} waiting
        {queue.oldestRunnableSeconds !== null ? `, the oldest for ${ago(queue.oldestRunnableSeconds).replace(" ago", "")}` : ""}.
        {" "}Contact support.
      </div>
    );
  }

  return (
    <p className="muted small processor">
      <span className={`dot dot--${worker.status === "ok" ? "ok" : "off"}`} aria-hidden="true" />
      {worker.status === "ok"
        ? `Processor online · ${worker.busy ? "working" : "idle"}`
        : "Processor offline · nothing is waiting"}
      {failed}
    </p>
  );
}
